<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Offerings</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage product offerings and variants</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isTableLoading"
                                                @click="refreshData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isTableLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search offerings..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <select
                                                v-model="stockFilter"
                                                class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-[8rem]"
                                        >
                                                <option value="all">All Stock</option>
                                                <option value="inStock">In Stock</option>
                                                <option value="outOfStock">Out of Stock</option>
                                        </select>
                                        <select
                                                v-model="translationFilter"
                                                class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-[10rem]"
                                        >
                                                <option value="all">All Translations</option>
                                                <option value="complete">Complete</option>
                                                <option value="partial">Partial</option>
                                                <option value="none">None</option>
                                        </select>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ totalRows }} offerings
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isTableLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading offerings...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Image</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">External ID</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">In Stock</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Translations</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="filteredData.length === 0">
                                                                <td colspan="7" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No offerings found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Try adjusting your search or filter</p>
                                                                </td>
                                                        </tr>
                                                        <template v-for="item in paginatedData" :key="item.id">
                                                                <tr
                                                                        class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                        @click="editOffering(item)"
                                                                >
                                                                        <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                        <td class="py-3 px-4">
                                                                                <div class="relative inline-block">
                                                                                        <img
                                                                                                v-if="item.thumbnail"
                                                                                                :src="item.thumbnail"
                                                                                                :alt="item.name || 'Offering'"
                                                                                                class="w-12 h-12 object-cover rounded-md border border-gray-200"
                                                                                        >
                                                                                        <div v-else class="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                                                                                                <svg class="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                                </svg>
                                                                                        </div>
                                                                                        <button
                                                                                                v-if="hasFilestackClient"
                                                                                                type="button"
                                                                                                :disabled="uploadingImageId === item.id"
                                                                                                class="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-7 border-2 border-white rounded-full flex items-center justify-center cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
                                                                                                title="Upload new image"
                                                                                                @click.stop="openImagePicker(item)"
                                                                                        >
                                                                                                <svg v-if="uploadingImageId === item.id" class="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                                </svg>
                                                                                                <svg v-else class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-sm font-medium text-gray-900">{{ item.name || item.header || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-500">{{ item.externalid || '-' }}</td>
                                                                        <td class="py-3 px-4 text-center">
                                                                                <span
                                                                                        class="inline-flex items-center py-1 px-2 text-xs font-medium rounded-full"
                                                                                        :class="item.instock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                                                                                >
                                                                                        {{ item.instock ? 'Yes' : 'No' }}
                                                                                </span>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-center" @click.stop>
                                                                                <div class="flex items-center justify-center gap-2">
                                                                                        <span
                                                                                                class="inline-flex items-center py-1 px-2 text-xs font-medium rounded-full"
                                                                                                :class="getTranslationStatusClass(item.id)"
                                                                                                :title="getTranslationStatusTitle(item.id)"
                                                                                        >
                                                                                                {{ getTranslationStatusLabel(item.id) }}
                                                                                        </span>
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7 hover:bg-orange-1"
                                                                                                title="Expand translations"
                                                                                                @click.stop="toggleTranslationExpand(item.id)"
                                                                                        >
                                                                                                <svg 
                                                                                                        class="w-4 h-4 transition-transform"
                                                                                                        :class="expandedRowIds.has(item.id) ? 'rotate-180' : ''"
                                                                                                        fill="none" 
                                                                                                        viewBox="0 0 24 24" 
                                                                                                        stroke="currentColor" 
                                                                                                        stroke-width="2"
                                                                                                >
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-right">
                                                                                <div class="flex justify-end gap-2">
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                                title="Edit"
                                                                                                @click.stop="editOffering(item)"
                                                                                        >
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                                <!-- Expanded Translation Row -->
                                                                <tr v-if="expandedRowIds.has(item.id)" class="bg-gray-50">
                                                                        <td colspan="7" class="p-0">
                                                                                <div class="p-4 border-t border-gray-200">
                                                                                        <!-- Loading Translations -->
                                                                                        <div v-if="isLoadingTranslations" class="flex items-center justify-center py-8">
                                                                                                <svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                                </svg>
                                                                                                <span class="ml-2 text-gray-500 text-sm">Loading translations...</span>
                                                                                        </div>
                                                                                        
                                                                                        <!-- Translation Editor -->
                                                                                        <div v-else>
                                                                                                <!-- Language Tabs -->
                                                                                                <div class="flex border-b border-gray-200 mb-4 overflow-x-auto">
                                                                                                        <button
                                                                                                                v-for="lang in languages"
                                                                                                                :key="lang.id"
                                                                                                                type="button"
                                                                                                                class="py-2 px-4 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                                                                                :class="selectedLanguageId === lang.id ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'"
                                                                                                                @click="selectedLanguageId = lang.id"
                                                                                                        >
                                                                                                                {{ lang.name }}
                                                                                                                <span
                                                                                                                        v-if="getLanguageTranslationStatus(item.id, lang.id) === 'complete'"
                                                                                                                        class="ml-1 w-2 h-2 rounded-full bg-green-500 inline-block"
                                                                                                                ></span>
                                                                                                                <span
                                                                                                                        v-else-if="getLanguageTranslationStatus(item.id, lang.id) === 'partial'"
                                                                                                                        class="ml-1 w-2 h-2 rounded-full bg-orange-500 inline-block"
                                                                                                                ></span>
                                                                                                                <span
                                                                                                                        v-else
                                                                                                                        class="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block"
                                                                                                                ></span>
                                                                                                        </button>
                                                                                                </div>
                                                                                                
                                                                                                <!-- Translation Fields -->
                                                                                                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                                                        <div>
                                                                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                                                                                <input
                                                                                                                        type="text"
                                                                                                                        :value="getTranslationField(item.id, selectedLanguageId!, 'name')"
                                                                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-1 focus:ring-orange-7"
                                                                                                                        placeholder="Enter name translation"
                                                                                                                        @input="updateTranslationField(item.id, selectedLanguageId!, 'name', ($event.target as HTMLInputElement).value)"
                                                                                                                >
                                                                                                        </div>
                                                                                                        <div>
                                                                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Variant Name</label>
                                                                                                                <input
                                                                                                                        type="text"
                                                                                                                        :value="getTranslationField(item.id, selectedLanguageId!, 'variantname')"
                                                                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-1 focus:ring-orange-7"
                                                                                                                        placeholder="Enter variant name translation"
                                                                                                                        @input="updateTranslationField(item.id, selectedLanguageId!, 'variantname', ($event.target as HTMLInputElement).value)"
                                                                                                                >
                                                                                                        </div>
                                                                                                        <div>
                                                                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                                                                                                <textarea
                                                                                                                        :value="getTranslationField(item.id, selectedLanguageId!, 'lines')"
                                                                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-1 focus:ring-orange-7 resize-none"
                                                                                                                        rows="3"
                                                                                                                        placeholder="Enter description (one line per item)"
                                                                                                                        @input="updateTranslationField(item.id, selectedLanguageId!, 'lines', ($event.target as HTMLTextAreaElement).value)"
                                                                                                                ></textarea>
                                                                                                                <p class="text-xs text-gray-400 mt-1">Each line will be saved as a separate item</p>
                                                                                                        </div>
                                                                                                </div>
                                                                                                
                                                                                                <!-- Actions -->
                                                                                                <div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                                                                                                        <div class="flex items-center gap-2">
                                                                                                                <span
                                                                                                                        v-if="hasUnsavedChanges(item.id)"
                                                                                                                        class="text-xs bg-amber-100 text-amber-700 py-1 px-2 rounded-full font-medium"
                                                                                                                >
                                                                                                                        Unsaved changes
                                                                                                                </span>
                                                                                                        </div>
                                                                                                        <div class="flex gap-2">
                                                                                                                <button
                                                                                                                        type="button"
                                                                                                                        class="py-2 px-4 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                                                                                                                        @click="expandedRowIds.delete(item.id)"
                                                                                                                >
                                                                                                                        Cancel
                                                                                                                </button>
                                                                                                                <button
                                                                                                                        type="button"
                                                                                                                        :disabled="!hasUnsavedChanges(item.id) || isSavingTranslations"
                                                                                                                        class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                                                        @click="saveTranslations(item.id)"
                                                                                                                >
                                                                                                                        {{ isSavingTranslations ? 'Saving...' : 'Save Translations' }}
                                                                                                                </button>
                                                                                                        </div>
                                                                                                </div>
                                                                                        </div>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        </template>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
                                        <p class="text-sm text-gray-500 m-0">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredData.length) }} of {{ filteredData.length }} results
                                        </p>
                                        <div class="flex gap-1">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage > 1 && currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer transition-colors"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage < totalPages && currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import OfferingService from 'services/OfferingService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';
import { httpClient } from 'utils/http';

interface Offering {
        id: number;
        name?: string;
        header?: string;
        externalid?: string;
        instock?: number;
        thumbnail?: string;
}

interface Language {
        id: number;
        name: string;
        default?: boolean;
}

interface TranslationEntry {
        name: string;
        variantname: string;
        lines: string;
}

type TranslationsMap = Record<number, Record<number, TranslationEntry>>;

export default defineComponent({
        name: 'OfferingsPage',
        setup() {
                const router = useRouter();
                const auth0 = useAuth0();
                const instance = getCurrentInstance();
                const filestackClient = instance?.appContext.config.globalProperties.$client as any;

                const offerings = ref<Offering[]>([]);
                const languages = ref<Language[]>([]);
                const translations = ref<TranslationsMap>({});
                const originalTranslations = ref<TranslationsMap>({});
                const isTableLoading = ref<boolean>(true);
                const isLoadingTranslations = ref<boolean>(false);
                const isSavingTranslations = ref<boolean>(false);
                const searchQuery = ref<string>('');
                const stockFilter = ref<string>('inStock');
                const translationFilter = ref<string>('all');
                const currentPage = ref<number>(1);
                const pageSize: number = 25;
                const uploadingImageId = ref<number | null>(null);
                const expandedRowIds = ref<Set<number>>(new Set<number>());
                const selectedLanguageId = ref<number | null>(null);

                const toastMessage = ref<string>('');
                const toastVariant = ref<string>('success');
                let toastTimeout: ReturnType<typeof setTimeout> | undefined;

                const loggedIn = computed(() => auth0.isAuthenticated.value);

                const getTranslationStatusForFilter = (offeringId: number): string => {
                        if (languages.value.length === 0) return 'none';
                        
                        let completeCount = 0;
                        
                        languages.value.forEach((lang: Language) => {
                                const trans = translations.value[offeringId]?.[lang.id];
                                if (trans) {
                                        const name = (trans.name || '').trim();
                                        const variantname = (trans.variantname || '').trim();
                                        const lines = (trans.lines || '').trim();
                                        if (name || variantname || lines) completeCount++;
                                }
                        });
                        
                        if (completeCount === languages.value.length) return 'complete';
                        if (completeCount > 0) return 'partial';
                        return 'none';
                };

                const filteredData = computed(() => {
                        let result = offerings.value;
                        
                        if (stockFilter.value === 'inStock') {
                                result = result.filter((item: Offering) => item.instock === 1);
                        } else if (stockFilter.value === 'outOfStock') {
                                result = result.filter((item: Offering) => item.instock !== 1);
                        }
                        
                        if (searchQuery.value) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter((item: Offering) =>
                                        (item.name && item.name.toLowerCase().includes(query)) ||
                                        (item.header && item.header.toLowerCase().includes(query)) ||
                                        (item.externalid && String(item.externalid).toLowerCase().includes(query)) ||
                                        (item.id && String(item.id).includes(query))
                                );
                        }
                        
                        if (translationFilter.value !== 'all') {
                                result = result.filter((item: Offering) => {
                                        const status = getTranslationStatusForFilter(item.id);
                                        return status === translationFilter.value;
                                });
                        }
                        
                        return result;
                });

                const totalRows = computed(() => filteredData.value.length);
                const totalPages = computed(() => Math.ceil(filteredData.value.length / pageSize));

                const paginatedData = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredData.value.slice(start, start + pageSize);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const showToast = (message: string, variant: string = 'success'): void => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = (): void => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const loadLanguages = async (): Promise<void> => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                languages.value = await LanguageService.getAll(params) as unknown as Language[];
                                if (languages.value.length > 0) {
                                        const defaultLang = languages.value.find((l: Language) => l.default);
                                        selectedLanguageId.value = defaultLang ? defaultLang.id : languages.value[0].id;
                                }
                        } catch (error) {
                                console.error('Failed to load languages:', error);
                        }
                };

                const loadData = async (): Promise<void> => {
                        if (!loggedIn.value) return;

                        isTableLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const data = await OfferingService.getAll(params);
                                offerings.value = data;
                                currentPage.value = 1;
                                
                                await loadTranslationsForOfferings();
                        } catch (error: any) {
                                showToast(error.message || 'Failed to load offerings', 'error');
                        } finally {
                                isTableLoading.value = false;
                        }
                };

                const loadTranslationsForOfferings = async (): Promise<void> => {
                        if (languages.value.length === 0 || offerings.value.length === 0) return;

                        try {
                                const langIds = languages.value.map((l: Language) => l.id).join(' ');
                                const params = new URLSearchParams({
                                        ns: 'offerings',
                                        lng: langIds,
                                        limit: '0',
                                });
                                const data = await TranslationService.getMultiTranslation(params);

                                const newTranslations: TranslationsMap = {};
                                offerings.value.forEach((offering: Offering) => {
                                        newTranslations[offering.id] = {};
                                        languages.value.forEach((lang: Language) => {
                                                const langData = (data[lang.id] as any)?.offerings?.[offering.id];
                                                newTranslations[offering.id][lang.id] = {
                                                        name: langData?.name || '',
                                                        variantname: langData?.variantname || '',
                                                        lines: Array.isArray(langData?.lines) ? langData.lines.join('\n') : '',
                                                };
                                        });
                                });
                                translations.value = newTranslations;
                                originalTranslations.value = JSON.parse(JSON.stringify(newTranslations));
                        } catch (error) {
                                console.error('Failed to load translations:', error);
                        }
                };

                const loadTranslationsForSingleOffering = async (offeringId: number): Promise<void> => {
                        if (languages.value.length === 0) return;

                        isLoadingTranslations.value = true;
                        try {
                                const langIds = languages.value.map((l: Language) => l.id).join(' ');
                                const params = new URLSearchParams({
                                        ns: 'offerings',
                                        lng: langIds,
                                        limit: '0',
                                });
                                const data = await TranslationService.getMultiTranslation(params);

                                if (!translations.value[offeringId]) {
                                        translations.value[offeringId] = {};
                                }
                                
                                languages.value.forEach((lang: Language) => {
                                        const langData = (data[lang.id] as any)?.offerings?.[offeringId];
                                        translations.value[offeringId][lang.id] = {
                                                name: langData?.name || '',
                                                variantname: langData?.variantname || '',
                                                lines: Array.isArray(langData?.lines) ? langData.lines.join('\n') : '',
                                        };
                                });
                                originalTranslations.value[offeringId] = JSON.parse(JSON.stringify(translations.value[offeringId]));
                        } catch (error: any) {
                                showToast(error.message || 'Failed to load translations', 'error');
                        } finally {
                                isLoadingTranslations.value = false;
                        }
                };

                const toggleTranslationExpand = async (offeringId: number): Promise<void> => {
                        if (expandedRowIds.value.has(offeringId)) {
                                expandedRowIds.value.delete(offeringId);
                        } else {
                                expandedRowIds.value.add(offeringId);
                                if (!translations.value[offeringId] || Object.keys(translations.value[offeringId]).length === 0) {
                                        await loadTranslationsForSingleOffering(offeringId);
                                }
                        }
                };

                const getTranslationField = (offeringId: number, langId: number, field: string): string => {
                        return (translations.value[offeringId]?.[langId] as any)?.[field] || '';
                };

                const updateTranslationField = (offeringId: number, langId: number, field: string, value: string): void => {
                        if (!translations.value[offeringId]) {
                                translations.value[offeringId] = {};
                        }
                        if (!translations.value[offeringId][langId]) {
                                translations.value[offeringId][langId] = { name: '', variantname: '', lines: '' };
                        }
                        (translations.value[offeringId][langId] as any)[field] = value;
                };

                const hasUnsavedChanges = (offeringId: number): boolean => {
                        const current = translations.value[offeringId];
                        const original = originalTranslations.value[offeringId];
                        if (!current || !original) return false;
                        return JSON.stringify(current) !== JSON.stringify(original);
                };

                const getLanguageTranslationStatus = (offeringId: number, langId: number): string => {
                        const trans = translations.value[offeringId]?.[langId];
                        if (!trans) return 'none';
                        
                        const name = (trans.name || '').trim();
                        const variantname = (trans.variantname || '').trim();
                        const lines = (trans.lines || '').trim();
                        
                        const hasAnyField = name || variantname || lines;
                        
                        if (hasAnyField) return 'complete';
                        return 'none';
                };

                const getTranslationStatus = (offeringId: number): string => {
                        if (languages.value.length === 0) return 'none';
                        
                        let completeCount = 0;
                        let partialCount = 0;
                        
                        languages.value.forEach((lang: Language) => {
                                const status = getLanguageTranslationStatus(offeringId, lang.id);
                                if (status === 'complete') completeCount++;
                                else if (status === 'partial') partialCount++;
                        });
                        
                        if (completeCount === languages.value.length) return 'complete';
                        if (completeCount > 0 || partialCount > 0) return 'partial';
                        return 'none';
                };

                const getTranslationStatusClass = (offeringId: number): string => {
                        const status = getTranslationStatus(offeringId);
                        if (status === 'complete') return 'bg-green-100 text-green-800';
                        if (status === 'partial') return 'bg-orange-100 text-orange-800';
                        return 'bg-red-100 text-red-800';
                };

                const getTranslationStatusLabel = (offeringId: number): string => {
                        const status = getTranslationStatus(offeringId);
                        if (status === 'complete') return 'Complete';
                        if (status === 'partial') return 'Partial';
                        return 'None';
                };

                const getTranslationStatusTitle = (offeringId: number): string => {
                        const status = getTranslationStatus(offeringId);
                        if (status === 'complete') return 'All translations provided for all languages';
                        if (status === 'partial') return 'Some translations are missing';
                        return 'No translations provided';
                };

                const saveTranslations = async (offeringId: number): Promise<void> => {
                        isSavingTranslations.value = true;
                        try {
                                for (const lang of languages.value) {
                                        const trans = translations.value[offeringId]?.[lang.id];
                                        if (trans) {
                                                const linesArray = trans.lines ? trans.lines.split('\n').filter((line: string) => line.trim()) : [];
                                                
                                                const bundle = {
                                                        [offeringId]: {
                                                                name: trans.name || '',
                                                                variantname: trans.variantname || '',
                                                                lines: linesArray,
                                                        },
                                                };

                                                await httpClient.put('/api/translation', {
                                                        language: lang.id,
                                                        namespace: 'offerings',
                                                        bundle,
                                                });
                                        }
                                }

                                originalTranslations.value[offeringId] = JSON.parse(JSON.stringify(translations.value[offeringId]));
                                showToast('Translations saved successfully');
                        } catch (error: any) {
                                showToast(error.message || 'Failed to save translations', 'error');
                        } finally {
                                isSavingTranslations.value = false;
                        }
                };

                const refreshData = (): void => {
                        loadData();
                };

                const editOffering = (item: Offering): void => {
                        router.push(`/offerings/${item.id}`);
                };

                const hasFilestackClient = computed(() => !!filestackClient);

                const openImagePicker = (item: Offering): void => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        uploadingImageId.value = item.id;
                        const options = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: async (files: { filesUploaded?: Array<{ url: string }> }) => {
                                        if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                const newUrl = files.filesUploaded[0].url;
                                                try {
                                                        await OfferingService.update(item.id, { thumbnail: newUrl });
                                                        item.thumbnail = newUrl;
                                                        showToast('Image updated successfully', 'success');
                                                } catch (error: any) {
                                                        showToast(error.message || 'Failed to update image', 'error');
                                                }
                                        }
                                        uploadingImageId.value = null;
                                },
                                onClose: () => {
                                        uploadingImageId.value = null;
                                },
                        };
                        filestackClient.picker(options).open();
                };

                watch(loggedIn, async (isLoggedIn: boolean) => {
                        if (isLoggedIn) {
                                await loadLanguages();
                                loadData();
                        }
                }, { immediate: true });

                watch(searchQuery, () => {
                        currentPage.value = 1;
                });

                watch(stockFilter, () => {
                        currentPage.value = 1;
                });

                watch(translationFilter, () => {
                        currentPage.value = 1;
                });

                return {
                        offerings,
                        languages,
                        translations,
                        isTableLoading,
                        isLoadingTranslations,
                        isSavingTranslations,
                        searchQuery,
                        stockFilter,
                        translationFilter,
                        currentPage,
                        pageSize,
                        totalRows,
                        totalPages,
                        filteredData,
                        paginatedData,
                        visiblePages,
                        uploadingImageId,
                        expandedRowIds,
                        selectedLanguageId,
                        toastMessage,
                        toastVariant,
                        hasFilestackClient,
                        loadData,
                        refreshData,
                        editOffering,
                        openImagePicker,
                        hideToast,
                        toggleTranslationExpand,
                        getTranslationField,
                        updateTranslationField,
                        hasUnsavedChanges,
                        getLanguageTranslationStatus,
                        getTranslationStatus,
                        getTranslationStatusClass,
                        getTranslationStatusLabel,
                        getTranslationStatusTitle,
                        saveTranslations,
                };
        },
});
</script>
